/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import {
  ApolloCache,
  MutationUpdaterFunction,
  QueryHookOptions,
  QueryResult,
  WatchQueryFetchPolicy,
} from "@apollo/client";
import {
  UpduoApolloClient,
  SkalePrimaryApolloClient,
} from "config/UpduoApolloClient";

const lastFetchedTime = new Map<any, number>();

function getFetchPolicyForKey(
  key: any,
  expirationMs: number
): WatchQueryFetchPolicy {
  const now = Date.now();
  const lastSuccessfulDownload = lastFetchedTime[key];
  const timeSinceLastDownload =
    lastSuccessfulDownload === undefined
      ? undefined
      : now - lastSuccessfulDownload;

  if (
    timeSinceLastDownload === undefined ||
    timeSinceLastDownload > expirationMs
  ) {
    return "network-only";
  }

  return "cache-first";
}

type QueryHookFunction<QueryType, QueryVariablesType, DataType> = (
  baseOptions: QueryHookOptions<QueryType, QueryVariablesType>
) => QueryResult<DataType, QueryVariablesType>;

type CachedQueryHookFunction<QueryType, QueryVariablesType, DataType> = (
  baseOptions: Omit<
    QueryHookOptions<QueryType, QueryVariablesType>,
    "fetchPolicy"
  >
) => QueryResult<DataType, QueryVariablesType>;

export function cached<QueryType, QueryVariablesType, DataType>(
  queryHookFunction: QueryHookFunction<QueryType, QueryVariablesType, DataType>,
  cacheTTLMS: number
): CachedQueryHookFunction<QueryType, QueryVariablesType, DataType> {
  return (options) => {
    const fetchPolicy = options.skip
      ? "cache-first"
      : getFetchPolicyForKey(queryHookFunction, cacheTTLMS);
    return queryHookFunction({
      ...options,
      fetchPolicy,
      onCompleted: (data) => {
        if (fetchPolicy === "network-only") {
          //@ts-ignore
          lastFetchedTime[queryHookFunction] = Date.now();
        }
        if (options.onCompleted) options.onCompleted(data);
      },
    });
  };
}

export function manuallyInvalidateQueries(queries: string[] | string) {
  const invalidate = invalidateQueries(queries);
  invalidate(UpduoApolloClient.cache, {}, {});
  invalidate(SkalePrimaryApolloClient.cache, {}, {});
}

export function invalidateQueries<TCache extends ApolloCache<unknown>>(
  queries: string[] | string
): MutationUpdaterFunction<unknown, unknown, unknown, TCache> {
  if (!Array.isArray(queries)) {
    queries = [queries];
  }
  return (cache: TCache) => {
    //@ts-ignore
    const rootQuery = cache.data?.data?.ROOT_QUERY;
    if (!rootQuery) return;
    let updated = false;
    for (const key of Object.keys(rootQuery)) {
      for (const query of queries) {
        const re = new RegExp(`^${query}[(:]`);
        if (key === query || key.match(re)) {
          delete rootQuery[key];
          updated = true;
          //console.log(`purging '${key}' from apollo ROOT_QUERY cache`);
        }
      }
    }
    if (updated) {
      cache.gc();
    }
  };
}

export const seconds = (x: number) => x * 1000;
export const minutes = (x: number) => x * 1000 * 60;
export const hours = (x: number) => x * 1000 * 60 * 60;
